import { FC } from 'react';
import { ArrowDownIcon, ArrowUpIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Sparkline } from './Sparkline';
import styles from './AthleteCard.module.scss';

export interface AthleteCardData {
  name: string;
  role?: string;
  age?: number;
  hrv?: { current: number; baseline?: number; delta?: number };
  ri?: number;
  rpe?: number;
  spark?: number[];
  note?: string;
  severity?: 'warn' | 'mild' | 'ok';
  actions?: string[];
}

const severityColor: Record<NonNullable<AthleteCardData['severity']>, string> = {
  warn: 'var(--c-error)',
  mild: 'var(--c-warning)',
  ok: 'var(--c-success)',
};

export const AthleteCard: FC<{ athlete: AthleteCardData }> = ({ athlete }) => {
  const initials = athlete.name
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const sevColor = severityColor[athlete.severity ?? 'mild'];
  const delta = athlete.hrv?.delta;
  const deltaPositive = delta != null && delta >= 0;

  return (
    <div className={styles.card}>
      <div className={styles.severityBar} style={{ background: sevColor }} />

      <div className={styles.left}>
        <div className={styles.avatar}>{initials}</div>
        <div className={styles.identity}>
          <div className={styles.name}>{athlete.name}</div>
          {(athlete.role || athlete.age != null) && (
            <div className={styles.meta}>
              {athlete.role}
              {athlete.role && athlete.age != null && <span> · </span>}
              {athlete.age != null && <span>{athlete.age} anni</span>}
            </div>
          )}
        </div>
      </div>

      <div className={styles.metrics}>
        {athlete.hrv?.current != null && (
          <div className={styles.kpi}>
            <div className={styles.kpiValue}>
              <span className={styles.kpiNumber}>{athlete.hrv.current.toFixed(1)}</span>
              <span className={styles.kpiUnit}>ms</span>
            </div>
            {athlete.hrv.baseline != null && (
              <div className={styles.kpiSub}>media {athlete.hrv.baseline.toFixed(1)}</div>
            )}
          </div>
        )}
        {athlete.ri != null && athlete.hrv?.current == null && (
          <div className={styles.kpi}>
            <div className={styles.kpiValue}>
              <span className={styles.kpiNumber}>{athlete.ri.toFixed(1)}</span>
              <span className={styles.kpiUnit}>RI</span>
            </div>
          </div>
        )}

        {athlete.spark && athlete.spark.length > 1 && (
          <div className={styles.spark}>
            <Sparkline data={athlete.spark} stroke={sevColor} fill={sevColor} width={84} height={28} />
          </div>
        )}

        {delta != null && (
          <span
            className={styles.deltaChip}
            style={{
              background: deltaPositive ? 'var(--c-success-bg)' : 'var(--c-error-bg)',
              color: deltaPositive ? 'var(--c-success)' : 'var(--c-error)',
            }}
          >
            {deltaPositive ? <ArrowUpIcon size={10} weight="bold" /> : <ArrowDownIcon size={10} weight="bold" />}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}

        <button className={styles.chevron} type="button" title="Apri scheda atleta">
          <CaretRightIcon size={14} />
        </button>
      </div>

      {athlete.note && <div className={styles.note}>{athlete.note}</div>}

      {athlete.actions && athlete.actions.length > 0 && (
        <ul className={styles.actions}>
          {athlete.actions.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
